"use client";

import React, { createContext, use, useState, useCallback, useRef, useEffect } from 'react';
import { useAudioPlayer } from '@/hooks/useAudioPlayer';
import { useAudioUrl } from '@/hooks/useAudioUrl';
import { audioPreloader } from '@/lib/audio-preloader';
import { playbackAudioSource } from '@/lib/sound-audio-source';
import {
  recordCompletion,
  recordSkip,
  SKIP_THRESHOLD,
} from '@/lib/playback-events';
import { announce, playerAnnouncements } from '@/lib/announce';
import { previewFailureMessage } from '@/lib/state-copy';
import type { Sound } from '@/lib/supabase-types';

const isDev = process.env.NODE_ENV === 'development';

interface AudioPlayerContextValue {
  // Current playback state
  currentSound: Sound | null;
  isPlaying: boolean;
  isLoading: boolean;
  currentTime: number;
  duration: number;
  progress: number;
  volume: number;
  error: string | null;

  // Queue management
  queue: Sound[];
  queueIndex: number;

  // Playback controls
  play: (sound: Sound) => void;
  pause: () => void;
  resume: () => void;
  toggle: () => void;
  seek: (time: number) => void;
  setVolume: (volume: number) => void;

  // Queue controls
  next: () => void;
  previous: () => void;
  addToQueue: (sound: Sound) => void;
  clearQueue: () => void;
  playQueue: (sounds: Sound[], startIndex?: number) => void;

  // UI state
  isExpanded: boolean;
  setIsExpanded: (expanded: boolean) => void;

  /** The live <audio> element, for §11.3 envelope-follower analysis only. */
  audioElement: HTMLAudioElement | null;
}

const AudioPlayerContext = createContext<AudioPlayerContextValue | undefined>(undefined);

// eslint-disable-next-line react-doctor/prefer-useReducer -- large provider with distinct state slices; refactor deferred
// eslint-disable-next-line react-doctor/no-giant-component -- provider component; size is inherent to the context contract
export function AudioPlayerProvider({ children }: { children: React.ReactNode }) {
  // Playback state
  const [currentSound, setCurrentSound] = useState<Sound | null>(null);
  // Lazy initializer reads persisted volume on first render so the React
  // state and audio element are seeded in sync — no setState-in-effect
  // shuffle on mount.
  const [volume, setVolumeState] = useState<number>(() => {
    if (typeof window === 'undefined') return 0.7;
    try {
      const saved = localStorage.getItem('audio-player-volume');
      if (saved) {
        const vol = parseFloat(saved);
        if (!isNaN(vol) && vol >= 0 && vol <= 1) return vol;
      }
    } catch {
      // localStorage unavailable — keep default
    }
    return 0.7;
  });
  const [isExpanded, setIsExpanded] = useState(false);

  // Preloaded audio element for instant playback
  const [preloadedElement, setPreloadedElement] = useState<HTMLAudioElement | null>(null);

  // Queue state
  const [queue, setQueue] = useState<Sound[]>([]);
  const [queueIndex, setQueueIndex] = useState(-1);

  // Track if user manually paused (to prevent auto-play on sound change)
  const userPausedRef = useRef(false);
  // The sound a play/next/previous/queue call has switched to, until its URL
  // arrives. Seeks made in that window are queued for this id.
  const newSoundIdRef = useRef<string | null>(null);

  // Refs for queue state to use in memoized callbacks
  const queueRef = useRef(queue);
  const queueIndexRef = useRef(queueIndex);

  // Refs for terminal-event classification (completion / skip) so the
  // callbacks see the latest values without re-rendering the provider.
  const currentSoundRef = useRef<Sound | null>(null);
  const currentTimeRef = useRef(0);
  const durationRef = useRef(0);
  const completedFiredRef = useRef(false); // dedupe per-sound completion

  // §11.2 "the interruption obligation": the name that was last announced as
  // playing, so that when sound B starts we can say "Stopped A. Playing B."
  // in one utterance rather than two independent announcements that could
  // arrive out of order.
  const lastAnnouncedPlayingNameRef = useRef<string | null>(null);
  // Set right before switching sounds so the play handler knows to announce
  // an interruption instead of a plain start.
  const interruptingFromRef = useRef<string | null>(null);
  // True for the one 'pause' event fired by play()'s own audioPause() call
  // when switching sounds — that pause is not a user action and must not
  // announce "Paused at N seconds" ahead of the interruption utterance.
  const suppressNextPauseAnnounceRef = useRef(false);

  // Sync refs with state in effect to avoid updating during render
  useEffect(() => {
    queueRef.current = queue;
  }, [queue]);

  useEffect(() => {
    queueIndexRef.current = queueIndex;
  }, [queueIndex]);

  // Get the playback URL for the current sound: its compressed preview when
  // it has one, else the WAV (through a server-resolved URL when the sound
  // came from an API that resolved one, TES-831, so playback never
  // re-resolves it client-side).
  const playbackSource = playbackAudioSource(currentSound);
  const { audioUrl, isLoading: audioUrlLoading } = useAudioUrl(
    playbackSource.fileKey,
    playbackSource.resolvedUrl
  );

  // Memoized callbacks for useAudioPlayer to prevent unnecessary re-renders
  const handleEnded = useCallback(() => {
    // The audio element reached its end — by definition this is a completion.
    // Record before mutating queue state so the recent-plays write captures
    // the sound that just finished, not the one we're about to start.
    const finished = currentSoundRef.current;
    if (finished && !completedFiredRef.current) {
      recordCompletion(finished, 'sound_player');
      completedFiredRef.current = true;
    }

    // Auto-play next sound in queue when current ends
    const currentQueue = queueRef.current;
    const currentIndex = queueIndexRef.current;
    if (currentIndex < currentQueue.length - 1) {
      const nextSound = currentQueue[currentIndex + 1];
      if (isDev) console.log('[AudioPlayerContext] Playing next from onEnded:', nextSound.title);
      setCurrentSound(nextSound);
      setQueueIndex(currentIndex + 1);
      userPausedRef.current = false;
      newSoundIdRef.current = nextSound.id;
    } else {
      // End of queue, reset
      userPausedRef.current = true;
    }
  }, []);

  // Deliberately not reported here. `useAudioPlayer` already captures the same
  // failure with the diagnostics that make it actionable (media-error code,
  // src, networkState, readyState) and drops the session conditions this
  // callback used to bill at `error` level; re-reporting the formatted string
  // was a second event per failure carrying strictly less. See TES-1977.
  const handleError = useCallback((err: string) => {
    if (isDev) console.log('[AudioPlayerContext] Playback error:', err);

    // §11.2's live-region contract: autoplay-blocked and "the preview died,
    // download still works" are two different sentences, never a generic one.
    // The same sentence is on the page (lib/state-copy.ts).
    announce(previewFailureMessage(err), 'polite');
  }, []);

  // §11.2: "Playback started" — one polite announcement per sound, and if a
  // different sound was mid-playback it becomes a single "Stopped A. Playing
  // B." utterance rather than two independent ones that could interleave.
  const handlePlay = useCallback(() => {
    const name = currentSoundRef.current?.title;
    if (!name) return;
    const stopped = interruptingFromRef.current;
    interruptingFromRef.current = null;
    if (stopped && stopped !== name) {
      announce(playerAnnouncements.interrupted(stopped, name), 'polite');
    } else {
      announce(playerAnnouncements.started(name), 'polite');
    }
    lastAnnouncedPlayingNameRef.current = name;
  }, []);

  const handlePause = useCallback(() => {
    if (suppressNextPauseAnnounceRef.current) {
      suppressNextPauseAnnounceRef.current = false;
      return;
    }
    if (!lastAnnouncedPlayingNameRef.current) return;
    announce(playerAnnouncements.paused(currentTimeRef.current), 'polite');
    // A paused sound is not "playing" — the next play() on a different
    // sound is a fresh start, not an interruption of this one.
    lastAnnouncedPlayingNameRef.current = null;
  }, []);

  const handleAudioEnded = useCallback(() => {
    announce(playerAnnouncements.ended(), 'polite');
    lastAnnouncedPlayingNameRef.current = null;
    handleEnded();
  }, [handleEnded]);

  // Audio player hook
  const {
    isPlaying,
    isLoading: audioIsLoading,
    currentTime,
    duration,
    progress,
    error,
    play: audioPlay,
    pause: audioPause,
    seek: audioSeek,
    queueSeek: audioQueueSeek,
    setVolume: audioSetVolume,
    audioElement,
  } = useAudioPlayer({
    src: audioUrl || undefined,
    soundId: currentSound?.id,
    volume,
    preloadedElement,
    onPlay: handlePlay,
    onPause: handlePause,
    onEnded: handleAudioEnded,
    onError: handleError,
  });

  // Play a new sound
  const play = useCallback((sound: Sound) => {
    if (isDev) console.log('[AudioPlayerContext] Playing sound:', sound.title);

    // If this is the same sound that's already loaded, just resume
    if (currentSound?.id === sound.id && audioUrl) {
      userPausedRef.current = false;
      audioPlay();
      return;
    }

    // Switching to a different sound — if the previous one was barely
    // played, classify as a skip. (Completion is handled in handleEnded.)
    const prev = currentSoundRef.current;
    const prevDur = durationRef.current;
    if (prev && prev.id !== sound.id && prevDur > 0 && !completedFiredRef.current) {
      const playedPct = currentTimeRef.current / prevDur;
      if (playedPct < SKIP_THRESHOLD) {
        recordSkip(prev, playedPct, 'sound_player');
      }
    }
    completedFiredRef.current = false;

    // If a different sound is currently playing, the next 'play' event is an
    // interruption, not a fresh start — announce it as one utterance.
    if (prev && prev.id !== sound.id && lastAnnouncedPlayingNameRef.current) {
      interruptingFromRef.current = lastAnnouncedPlayingNameRef.current;
      suppressNextPauseAnnounceRef.current = true;
    }

    // Stop any currently playing audio before switching to a new sound
    audioPause();

    // Check for preloaded audio element for instant playback
    const preloaded = audioPreloader.getPreloaded(sound.id);
    if (preloaded) {
      if (isDev) console.log('[AudioPlayerContext] Using preloaded audio - instant playback!');
      setPreloadedElement(preloaded);
    } else {
      // Clear any previous preloaded element
      setPreloadedElement(null);
    }

    setCurrentSound(sound);
    userPausedRef.current = false;
    newSoundIdRef.current = sound.id;

    // If this sound is not in queue, create a new queue with just this sound
    const soundInQueue = queue.find(s => s.id === sound.id);
    if (!soundInQueue) {
      setQueue([sound]);
      setQueueIndex(0);
    } else {
      // Find the index of this sound in the queue
      const index = queue.findIndex(s => s.id === sound.id);
      setQueueIndex(index);
    }
  }, [queue, currentSound?.id, audioUrl, audioPlay, audioPause]);

  // Pause current playback
  const pause = useCallback(() => {
    if (isDev) console.log('[AudioPlayerContext] Pausing');
    userPausedRef.current = true;
    audioPause();
  }, [audioPause]);

  // Resume current playback
  const resume = useCallback(async () => {
    if (isDev) console.log('[AudioPlayerContext] Resuming');
    userPausedRef.current = false;
    await audioPlay();
  }, [audioPlay]);

  // Toggle play/pause
  const toggle = useCallback(() => {
    if (isPlaying) {
      pause();
    } else {
      resume();
    }
  }, [isPlaying, pause, resume]);

  // Seek to time. §11.2: announced on release only, never during drag, and
  // throttled to 300ms so a keyboard-repeat seek doesn't queue an
  // announcement per keystroke.
  const seek = useCallback((time: number) => {
    if (isDev) console.log('[AudioPlayerContext] Seeking to:', time);
    // A seek straight after play() of a new sound belongs to that sound, whose
    // element does not exist yet; the hook starts playback there once loaded.
    if (newSoundIdRef.current !== null) {
      audioQueueSeek(time, newSoundIdRef.current);
    } else {
      audioSeek(time);
    }
    announce(playerAnnouncements.seeked(time, durationRef.current), 'polite', 300);
  }, [audioSeek, audioQueueSeek]);

  // Set volume
  const setVolume = useCallback((vol: number) => {
    const clampedVolume = Math.max(0, Math.min(1, vol));
    if (isDev) console.log('[AudioPlayerContext] Setting volume:', clampedVolume);
    setVolumeState(clampedVolume);
    audioSetVolume(clampedVolume);
  }, [audioSetVolume]);

  // Play next sound in queue
  const next = useCallback(() => {
    if (queueIndex < queue.length - 1) {
      const nextSound = queue[queueIndex + 1];
      if (isDev) console.log('[AudioPlayerContext] Playing next:', nextSound.title);
      setCurrentSound(nextSound);
      setQueueIndex((prev) => prev + 1);
      userPausedRef.current = false;
      newSoundIdRef.current = nextSound.id;
    }
  }, [queue, queueIndex]);

  // Play previous sound in queue
  const previous = useCallback(() => {
    // If we're more than 3 seconds into the current sound, restart it
    if (currentTime > 3) {
      audioSeek(0);
    } else if (queueIndex > 0) {
      const prevSound = queue[queueIndex - 1];
      if (isDev) console.log('[AudioPlayerContext] Playing previous:', prevSound.title);
      setCurrentSound(prevSound);
      setQueueIndex((prev) => prev - 1);
      userPausedRef.current = false;
      newSoundIdRef.current = prevSound.id;
    }
  }, [queue, queueIndex, currentTime, audioSeek]);

  // Add sound to queue
  const addToQueue = useCallback((sound: Sound) => {
    if (isDev) console.log('[AudioPlayerContext] Adding to queue:', sound.title);
    setQueue(prev => [...prev, sound]);
  }, []);

  // Clear queue
  // eslint-disable-next-line react-doctor/no-chain-state-updates -- resetting multiple independent slices atomically is intentional
  const clearQueue = useCallback(() => {
    if (isDev) console.log('[AudioPlayerContext] Clearing queue');
    setQueue([]);
    setQueueIndex(-1);
    setCurrentSound(null);
    userPausedRef.current = true;
  }, []);

  // Play a queue of sounds
  const playQueue = useCallback((sounds: Sound[], startIndex = 0) => {
    if (isDev) console.log('[AudioPlayerContext] Playing queue:', sounds.length, 'sounds, starting at index:', startIndex);
    setQueue(sounds);
    setQueueIndex(startIndex);
    setCurrentSound(sounds[startIndex]);
    userPausedRef.current = false;
    newSoundIdRef.current = sounds[startIndex]?.id ?? null;
  }, []);

  // Auto-play when audio URL is loaded and user hasn't paused
  // Using a ref for audioPlay to avoid effect re-running when the callback changes
  const audioPlayRef = useRef(audioPlay);

  // Sync ref with callback in effect to avoid updating during render
  useEffect(() => {
    audioPlayRef.current = audioPlay;
  }, [audioPlay]);

  // The URL arriving ends the switch whether or not it autoplays: a pause in
  // the gap leaves the sound loaded and paused, and later seeks go straight to
  // its element.
  useEffect(() => {
    if (!audioUrl) return;
    const autoplay = newSoundIdRef.current !== null && !userPausedRef.current;
    newSoundIdRef.current = null;
    if (autoplay) {
      if (isDev) console.log('[AudioPlayerContext] Auto-playing new sound');
      audioPlayRef.current();
    }
  }, [audioUrl]); // Removed audioPlay - using ref instead to prevent effect reset

  // Sync the audio element with the React volume state on mount. The state
  // itself was already hydrated from localStorage via the lazy initializer
  // above, so this effect only pushes that value down to the audio element —
  // no setState involved.
  useEffect(() => {
    audioSetVolume(volume);
    // Only on mount; subsequent user-driven volume changes route through the
    // `setVolume` callback below, which updates both React state and the
    // audio element together.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioSetVolume]);

  // eslint-disable-next-line react-doctor/no-effect-chain -- load and save effects are a valid localStorage persistence pattern; not a reactive chain
  useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem('audio-player-volume', volume.toString());
      } catch (error) {
        import('@sentry/browser').then(({ captureException }) => {
          captureException(error, { tags: { context: 'AudioPlayerContext', action: 'saveVolume' } });
        }).catch(() => {});
      }
    }
  }, [volume]);

  // Mirror state to refs so the terminal-event callbacks see the latest
  // values without resetting useCallback identities.
  useEffect(() => { currentSoundRef.current = currentSound; }, [currentSound]);
  useEffect(() => { currentTimeRef.current = currentTime; }, [currentTime]);
  useEffect(() => { durationRef.current = duration; }, [duration]);
  // Reset the "already-completed" dedupe flag whenever the sound changes.
  useEffect(() => { completedFiredRef.current = false; }, [currentSound?.id]);

  // Record a skip on page unload if the user leaves mid-listen with <20%
  // played. Uses pagehide (more reliable than beforeunload on mobile).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onPageHide = () => {
      const s = currentSoundRef.current;
      const d = durationRef.current;
      if (!s || d <= 0 || completedFiredRef.current) return;
      const playedPct = currentTimeRef.current / d;
      if (playedPct < SKIP_THRESHOLD) {
        recordSkip(s, playedPct, 'sound_player');
      }
    };
    window.addEventListener('pagehide', onPageHide);
    return () => window.removeEventListener('pagehide', onPageHide);
  }, []);

  const value: AudioPlayerContextValue = {
    currentSound,
    isPlaying,
    isLoading: audioUrlLoading || audioIsLoading,
    currentTime,
    duration,
    progress,
    volume,
    error,
    queue,
    queueIndex,
    play,
    pause,
    resume,
    toggle,
    seek,
    setVolume,
    next,
    previous,
    addToQueue,
    clearQueue,
    playQueue,
    isExpanded,
    setIsExpanded,
    audioElement,
  };

  return (
    <AudioPlayerContext.Provider value={value}>
      {children}
    </AudioPlayerContext.Provider>
  );
}

export function useAudioPlayerContext() {
  const context = use(AudioPlayerContext);
  if (context === undefined) {
    throw new Error('useAudioPlayerContext must be used within an AudioPlayerProvider');
  }
  return context;
}
